// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import { SUPPLIER_REFERRAL_REPORT_TYPE } from "@modules/reporting/constants/reports";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, View } from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import {
  selectedReferralDetails,
  showReferralDetailsModal,
  selectReferralCommonData,
} from "@modules/reporting/selectors/referral-tracker";
import {
  ReferralEmployeeDetailsSection,
  ReferralDetailsSection,
  ReferralQuestionsTable,
  ReferralAbsenceHistoryTable,
  ReferralLineManagersTable,
} from "@modules/shared/components";
import ReferralAcknowledgementSection from "./referral-acknowledgement-section";
import { selectUserType } from "@modules/account/selectors";
import { USER_TYPES } from "@modules/account/constants";

type OwnProps = $Exact<{
  data: number,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  isModalVisible: boolean,
  details: Object,
  supplierReferralId: ?number,
  referralType: string,
  selectUserType: string,
}>;

type PropsFromDispatch = $Exact<{
  toggleReferralDetailsModal: typeof actionCreators.ToggleSupplierReferralDetailsModal,
  submitSupplierReferralStatus: typeof actionCreators.SubmitSupplierReferralStatus,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ownState = $Exact<{
  supplierReferralStatus: string,
}>;

export class SupplierReferralDetailsModal extends Component<Props, ownState> {
  state = {
    supplierReferralStatus: "",
  };

  handleCancel = () => {
    this.setState({ supplierReferralStatus: "" });
    this.props.toggleReferralDetailsModal({});
  };

  save = () => {
    this.props.submitSupplierReferralStatus({
      clientId: this.props.clientId,
      supplierReferralStatus: this.state.supplierReferralStatus,
      supplierReferralId: this.props.supplierReferralId,
    });
  };

  onConfirmStatus = (status: string) => () => {
    this.setState({
      supplierReferralStatus: status,
    });
  };

  get isValid() {
    return this.state.supplierReferralStatus !== "";
  }
  get isNotFullHrAccessUser() {
    return this.props.selectUserType !== USER_TYPES.FULL_HR_ACCESS;
  }

  render() {
    return (
      this.props.isModalVisible && (
        <Modal className="modal-large">
          <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
            {this.props.t("REFERRAL_DETAILS_BY_ID", { id: this.props.supplierReferralId })}
          </ModalHeader>
          <ModalBody className="d-flex flex-column align-content-center pt-3 pb-4">
            <View gap={10}>
              <ReferralEmployeeDetailsSection
                t={this.props.t}
                data={this.props.details.employeeDetails}
              />
              <ReferralDetailsSection data={this.props.details.referralDetails} />
              <ReferralQuestionsTable data={this.props.details.referralQuestions} />
              <ReferralLineManagersTable data={this.props.details.lineManagers} />
              <ReferralAbsenceHistoryTable data={this.props.details.absenceHistory} />
              {this.isNotFullHrAccessUser && (
                <ReferralAcknowledgementSection
                  data={this.props.details}
                  confirmStatus={this.onConfirmStatus}
                  supplierReferralStatus={this.state.supplierReferralStatus}
                  referralType={this.props.referralType}
                />
              )}
            </View>
          </ModalBody>
          <ModalFooter className="d-flex justify-content-end">
            <Button color="secondary" onClick={this.handleCancel}>
              {this.props.t("CANCEL")}
            </Button>
            {this.props.referralType === SUPPLIER_REFERRAL_REPORT_TYPE.NEW_REFERRALS && (
              <Button materialIcon="save" onClick={this.save} disabled={!this.isValid}>
                {this.props.t("SAVE")}
              </Button>
            )}
          </ModalFooter>
        </Modal>
      )
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isModalVisible: showReferralDetailsModal(state),
    details: selectedReferralDetails(state),
    supplierReferralId: selectReferralCommonData(state).supplierReferralId,
    referralType: selectReferralCommonData(state).referralType,
    selectUserType: selectUserType(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleReferralDetailsModal: actionCreators.ToggleSupplierReferralDetailsModal,
  submitSupplierReferralStatus: actionCreators.SubmitSupplierReferralStatus,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(SupplierReferralDetailsModal);
